/**
 * ThinkingVoiceCommandService — detector de comandos rápidos por voz en el PIENSE.
 *
 * Captura habla vía Web Speech API (STT). La DETECCIÓN de comandos está migrada
 * de regex a LLM (decision-api, Nova Micro tool-use vía CommandClassifierService):
 * cada resultado FINAL corto del ASR en modo 'thinking' se clasifica con el modelo
 * y se despacha el comando (answer_now|pass|repeat|clarify). answer_now abre la
 * ventana de respuesta; pass/repeat/clarify cierran el piense y actúan sobre el turno.
 *
 * Solo clasifica en modo 'thinking' (el piense). En la RESPUESTA ('answering'/'off')
 * el front NO detecta comandos — LOW los clasifica server-side sobre el transcript
 * del turno. `onTranscript` sigue recibiendo CADA resultado (interim y final) para
 * las decisiones de silencio/afecto del caller, independiente de la clasificación.
 *
 * Riesgos conocidos y aceptados: segundo reconocimiento de voz en paralelo, solo
 * Chromium, el audio viaja a los servidores de Google. Chrome mata la sesión de
 * SpeechRecognition sola tras algunos segundos de silencio; onend reintenta
 * mientras la ventana siga abierta, acotado por windowMs (computeMaxRestarts).
 *
 * Degradación OBLIGATORIA: si el browser no soporta la API, si start() tira, si la
 * sesión muere y se agota el tope, o si el clasificador falla, el piense sigue por
 * tiempo — esta clase JAMÁS debe poder romper la entrevista.
 */
import { Injectable, NgZone, signal } from '@angular/core';
import {
  CommandClassifierService,
  ThinkingCommand,
} from 'src/app/services/command-classifier.service';

/** Opciones de start() — todas opcionales para no romper callers existentes. */
export interface ThinkingVoiceCommandStartOptions {
  /** Duración esperada de la ventana en ms — escala el presupuesto de reintentos. */
  windowMs?: number;
  /** Callback por CADA resultado (interim o final) — canal de silencio/afecto. */
  onTranscript?: (text: string, isFinal: boolean) => void;
  /**
   * Predicado de eco del TTS local (fuente única: FcLowRuntime.isLikelyEcho). Un
   * final que es el propio audio de Eva NUNCA se clasifica como comando (bug:
   * el recognizer oyó el ack "pasamos a la siguiente" de Eva).
   */
  isEcho?: (text: string) => boolean;
  /**
   * El recognizer de Chrome reinicia solo (onend → nuevo start): true entre el
   * 'end' y el 'start'/'audiostart'/primer resultado de la sesión nueva. El LOW
   * runtime lo usa para no cerrar el turno por silencio durante el hueco (1–3 s).
   */
  onRecognizerRestarting?: (restarting: boolean) => void;
}

const RESTARTS_MIN = 5;
/** 1 reintento adicional cada 20s de ventana esperada. */
const RESTART_ALLOWANCE_MS = 20_000;

function computeMaxRestarts(windowMs: number | undefined): number {
  if (!windowMs || windowMs <= 0) return RESTARTS_MIN;
  return Math.max(RESTARTS_MIN, Math.ceil(windowMs / RESTART_ALLOWANCE_MS));
}

const TRANSCRIPT_LOG_MAX_CHARS = 80;

// Item 3/5/8 (feedback QA 2026-09-29) — el cooldown POR TIEMPO (4000ms) que
// vivía acá no alcanzaba: Chrome mata la sesión de SpeechRecognition sola y
// reintenta (onend → attemptStart, ver mas abajo), y la sesión reiniciada
// puede volver a "escuchar" la cola de la MISMA frase ya despachada (quirk
// conocido de Web Speech en modo continuo). En produccion ese reintento tardo
// 17-40s en pasar — muy por encima del cooldown — asi que el comando
// (pass/repeat/clarify) se volvia a despachar, y el candidato veia/oia la
// pregunta repetirse. pass/repeat/clarify NUNCA llaman stop() (el ASR sigue
// corriendo a proposito para detectar comandos durante la respuesta), asi que
// subir el numero no cierra el hueco para cualquier duracion real de
// reintento. El propio docblock de la clase ya dice "el comando debe tomarse
// A LA PRIMERA" — eso es exactamente "una vez por ventana", no "una vez cada
// N ms", asi que el dedupe pasa a ser por VENTANA (Set, limpiado en
// start()/stop(), ver dispatchedThisWindow), no por tiempo.

/**
 * Una frase de comando es corta. Un final más largo que esto es contenido del
 * candidato pensando en voz alta → se salta la clasificación (no gasta LLM ni
 * confunde razonamiento con comando).
 */
const MAX_COMMAND_WORDS = 8;

/**
 * 'off': no clasifica. 'thinking': clasifica cada FINAL corto por LLM (el piense).
 * 'answering': el front NO clasifica comandos (LOW lo hace); solo onTranscript.
 */
export type CommandMatchingMode = 'off' | 'thinking' | 'answering';

@Injectable()
export class ThinkingVoiceCommandService {
  /**
   * Variantes answer_now usadas SOLO por stripAnswerNowCommand (limpieza del
   * transcript en LOW). La detección del comando la hace el clasificador LLM.
   */
  static readonly ANSWER_NOW_VARIANTS: readonly string[] = [
    'eva quiero contestar',
    'quiero contestar',
    'ya quiero contestar',
    'eva quiero responder',
    'quiero responder',
    'ya quiero responder',
  ];

  private static readonly ANSWER_NOW_VERBS: ReadonlySet<string> = new Set([
    'contestar',
    'responder',
  ]);

  private static readonly ANSWER_NOW_TOKENS: ReadonlySet<string> = new Set(
    ThinkingVoiceCommandService.ANSWER_NOW_VARIANTS.flatMap((v) => v.split(' ')),
  );

  private static readonly LEADING_FILLERS: ReadonlySet<string> = new Set([
    'bueno', 'este', 'esta', 'eh', 'em', 'mm', 'mmm', 'pues',
    'ok', 'okay', 'vale', 'mira', 'osea', 'ahm',
  ]);

  /**
   * Fast path instantáneo (recall + latencia): frases EXACTAS de comando
   * curadas del regex histórico — match determinista a ~0ms, sin round-trip al
   * LLM. Todo lo que NO matchee acá cae al clasificador LLM (jerga/variaciones).
   * Orden: clarify/repeat/pass antes de answer_now (histórico).
   */
  private static readonly INSTANT_COMMANDS: ReadonlyArray<{
    id: ThinkingCommand;
    variants: readonly string[];
  }> = [
    {
      id: 'clarify',
      variants: [
        'no entendi la pregunta',
        'no entendi la pregunta por favor',
        'reformulame la pregunta',
        'reformula la pregunta',
        'reformular la pregunta',
        'podes reformular la pregunta',
        'puedes reformular la pregunta',
        'reformulame',
        'reformula',
      ],
    },
    {
      id: 'context',
      variants: [
        'dame contexto',
        'dame contexto por favor',
        'dame mas contexto',
        'dame un poco de contexto',
        'dame un poco mas de contexto',
        'me das contexto',
        'me das un poco de contexto',
        'necesito contexto',
        'necesito mas contexto',
        'ponme en contexto',
      ],
    },
    {
      id: 'pause',
      variants: [
        'quiero una pausa',
        'quiero una pausa por favor',
        'necesito una pausa',
        'necesito una pausa por favor',
        'me das una pausa',
        'dame una pausa',
        'quisiera una pausa',
      ],
    },
    {
      id: 'repeat',
      variants: ['repite la pregunta', 'repiteme la pregunta', 'repite la pregunta por favor'],
    },
    {
      id: 'pass',
      variants: [
        'pasa a la siguiente pregunta',
        'pasa a la siguiente',
        'pasa a la siguiente pregunta por favor',
        'pasemos a la siguiente pregunta',
        'pasemos a la siguiente',
        'pasemos con la siguiente pregunta',
        'pasemos con la siguiente',
      ],
    },
    {
      id: 'answer_now',
      variants: ThinkingVoiceCommandService.ANSWER_NOW_VARIANTS,
    },
  ];

  private recognition: any = null;
  private active = false;
  private mode: CommandMatchingMode = 'thinking';
  /** Una clasificación LLM en vuelo a la vez; los finales que lleguen mientras
   *  tanto NO se descartan: quedan en pendingFinal (el más nuevo pisa al
   *  anterior) y se clasifican al liberarse el slot. */
  private classifyInFlight = false;
  private pendingFinal: string | null = null;
  /** Incrementa en start()/stop()/cambio de modo — un classify que resuelve
   *  con otra generación (o fuera de 'thinking') se descarta sin dispatch. */
  private generation = 0;
  private dispatchedThisWindow: Set<string> = new Set();
  private lastOnMatch: ((commandId: string) => void) | null = null;
  private lastOnTranscript: ((text: string, isFinal: boolean) => void) | null = null;
  private isEchoPredicate: ((text: string) => boolean) | null = null;
  private onRestartingChange: ((restarting: boolean) => void) | null = null;
  /** true entre el 'end' del recognizer y el 'start' de su reinicio. */
  readonly recognizerRestarting = signal(false);
  private restartCount = 0;
  private maxRestartsThisWindow = RESTARTS_MIN;

  constructor(
    private ngZone: NgZone,
    private classifier: CommandClassifierService,
  ) {}

  isSupported(): boolean {
    return !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);
  }

  /** Pre-warm del lambda decision (cold start) al arrancar la sesión FC — antes
   *  del primer piense. Fire-and-forget. */
  warmUpDecision(): void {
    this.classifier.warmUp();
  }

  isActive(): boolean {
    return this.active;
  }

  start(onMatch: (commandId: string) => void, opts: ThinkingVoiceCommandStartOptions = {}): void {
    if (this.active) return;
    if ((window as any).__karma_present || (window as any).__karma__) return;
    this.restartCount = 0;
    this.maxRestartsThisWindow = computeMaxRestarts(opts.windowMs);
    this.mode = 'thinking';
    this.generation++;
    this.classifyInFlight = false;
    this.pendingFinal = null;
    this.dispatchedThisWindow.clear();
    this.lastOnMatch = onMatch;
    this.lastOnTranscript = opts.onTranscript ?? null;
    this.isEchoPredicate = opts.isEcho ?? null;
    this.onRestartingChange = opts.onRecognizerRestarting ?? null;
    console.log(`[FC] voice_command_window_start maxRestarts=${this.maxRestartsThisWindow}`);
    this.attemptStart(onMatch, this.lastOnTranscript);
  }

  private attemptStart(
    onMatch: (commandId: string) => void,
    onTranscript: ((text: string, isFinal: boolean) => void) | null,
  ): void {
    const Ctor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!Ctor) {
      console.warn('[FC] voice_command_unsupported — Web Speech API no disponible, la entrevista corre completa');
      return;
    }

    try {
      const recognition = new Ctor();
      recognition.interimResults = true;
      recognition.continuous = true;
      recognition.lang = 'es-419';
      recognition.onstart = () => this.setRestarting(false);
      recognition.onaudiostart = () => this.setRestarting(false);
      recognition.onresult = (event: any) => {
        this.setRestarting(false);
        try {
          const results = event?.results;
          if (!results) return;
          const from = typeof event.resultIndex === 'number' ? event.resultIndex : 0;
          for (let i = from; i < results.length; i++) {
            const alt = results[i]?.[0];
            const transcript = alt?.transcript ?? '';
            const isFinal = !!results[i]?.isFinal;
            if (isFinal) {
              console.log('[FC] voice_command_transcript_final', transcript.slice(0, TRANSCRIPT_LOG_MAX_CHARS));
            }
            if (onTranscript) {
              try {
                onTranscript(transcript, isFinal);
              } catch (e) {
                console.warn('[FC] voice_command_ontranscript_error', e);
              }
            }
            // Solo el PIENSE clasifica comandos, solo en resultados FINALES.
            // La RESPUESTA la clasifica LOW sobre el transcript del turno.
            if (this.mode !== 'thinking' || !isFinal) continue;
            this.classifyFinalResult(transcript, onMatch);
          }
        } catch (e) {
          console.warn('[FC] voice_command_onresult_error', e);
        }
      };
      recognition.onerror = (e: any) => {
        console.warn('[FC] voice_command_recognition_error', e?.error ?? e);
      };
      recognition.onend = () => {
        this.active = false;
        console.log('[FC] voice_command_onend');
        if (!this.lastOnMatch) {
          console.log('[FC] voice_command_onend_no_window — no reintenta (ventana ya cerrada)');
          this.setRestarting(false);
          return;
        }
        if (this.restartCount >= this.maxRestartsThisWindow) {
          this.setRestarting(false);
          console.warn(
            `[FC] voice_command_restart_limit_reached (${this.maxRestartsThisWindow}) — desiste, la entrevista sigue su curso normal`,
          );
          return;
        }
        this.restartCount++;
        this.setRestarting(true);
        console.log(`[FC] voice_command_restart intento=${this.restartCount}/${this.maxRestartsThisWindow}`);
        this.attemptStart(this.lastOnMatch, this.lastOnTranscript);
      };
      recognition.start();
      this.recognition = recognition;
      this.active = true;
      console.log(`[FC] voice_command_started lang=${recognition.lang}`);
    } catch (e) {
      console.warn('[FC] voice_command_start_failed — la entrevista corre completa', e);
      this.setRestarting(false);
      this.active = false;
      this.recognition = null;
    }
  }

  private setRestarting(restarting: boolean): void {
    if (this.recognizerRestarting() === restarting) return;
    this.recognizerRestarting.set(restarting);
    try {
      this.onRestartingChange?.(restarting);
    } catch {
      /* el aviso al runtime nunca debe romper el recognizer */
    }
  }

  /**
   * Resuelve un resultado FINAL del piense en cascada — el comando debe tomarse
   * A LA PRIMERA:
   *  1. Fast path instantáneo (frase exacta, ~0ms, sin LLM).
   *  2. Final largo → rescate por TAIL (comando pegado a murmullo); si no, es
   *     contenido (no va al LLM).
   *  3. LLM fallback (jerga/variaciones). Sin drops: si hay classify en vuelo,
   *     el final queda pendiente (el más nuevo pisa al anterior) y se clasifica
   *     al liberarse el slot. Nunca lanza — fallo → "sin comando".
   */
  private classifyFinalResult(transcript: string, onMatch: (commandId: string) => void): void {
    const norm = this.normalize(transcript);
    if (!norm) return;

    // Eco del TTS de Eva: no es un comando del candidato.
    try {
      if (this.isEchoPredicate?.(transcript)) {
        console.log('[FC] asr_echo_dropped', {
          where: 'voice_command',
          text: transcript.slice(0, TRANSCRIPT_LOG_MAX_CHARS),
        });
        return;
      }
    } catch {
      /* degradación: sin predicado confiable se clasifica como siempre */
    }

    const instant = this.matchInstant(norm);
    if (instant) {
      this.dispatchCommand(instant, 'instant', onMatch);
      return;
    }

    const words = norm.split(/\s+/).filter(Boolean);
    if (words.length > MAX_COMMAND_WORDS) {
      // Rescate: el ASR suele pegar el comando al murmullo previo en un mismo
      // final ("y bueno entonces sí pasa a la siguiente pregunta"). Strip de
      // muletillas iniciales + fast path sobre el tail; si tampoco, es
      // contenido de pensar en voz alta — no se gasta LLM.
      const noFillers = this.stripLeadingFillers(words);
      const tail = noFillers.slice(-MAX_COMMAND_WORDS).join(' ');
      const tailMatch = tail ? this.matchInstant(tail) : null;
      if (tailMatch) this.dispatchCommand(tailMatch, 'instant-tail', onMatch);
      return;
    }

    if (this.classifyInFlight) {
      // No descartar: guardar el más reciente y clasificarlo al liberar el slot
      // (antes se perdía en silencio → el usuario tenía que repetir el comando).
      if (this.pendingFinal) console.log('[FC] voice_command_pending_final_replaced');
      this.pendingFinal = transcript;
      return;
    }
    this.launchClassify(transcript, onMatch);
  }

  /** Llama al LLM y despacha; al terminar drena el pendingFinal si sigue vigente. */
  private launchClassify(transcript: string, onMatch: (commandId: string) => void): void {
    this.classifyInFlight = true;
    const gen = this.generation;
    this.classifier
      .classifyThinkingCommand(transcript)
      .then((command: ThinkingCommand | null) => {
        // Stale-drop: el piense terminó / la ventana cambió mientras el LLM
        // respondía — despachar acá dispararía el comando fuera de fase (la
        // fuente de la carrera visual del avance).
        if (gen !== this.generation || this.mode !== 'thinking') {
          console.log('[FC] voice_command_classify_stale_dropped', command ?? 'none');
          return;
        }
        if (!command || command === 'none') return;
        this.dispatchCommand(command, 'llm', onMatch);
      })
      .catch((e) => {
        console.warn('[FC] voice_command_classify_error', e);
      })
      .finally(() => {
        this.classifyInFlight = false;
        const pending = this.pendingFinal;
        this.pendingFinal = null;
        if (pending && gen === this.generation && this.mode === 'thinking') {
          this.launchClassify(pending, onMatch);
        }
      });
  }

  /** Match instantáneo de frase exacta (word-boundary / allowlist corto de pass). */
  private matchInstant(norm: string): ThinkingCommand | null {
    const shortPass = this.matchShortPassAllowlist(norm);
    if (shortPass) return shortPass;
    for (const cmd of ThinkingVoiceCommandService.INSTANT_COMMANDS) {
      if (cmd.variants.some((v) => this.containsPhrase(norm, v))) return cmd.id;
    }
    return null;
  }

  /** Frases cortas de pass permitidas SOLO como utterance completo (≤4 palabras) —
   *  evita que "en la siguiente pregunta voy a explicar…" dispare pass. */
  private matchShortPassAllowlist(norm: string): ThinkingCommand | null {
    const words = norm.split(/\s+/).filter(Boolean);
    if (words.length > 4) return null;
    const stripped = norm.replace(/\s+por favor$/, '').trim();
    if (
      stripped === 'siguiente pregunta' ||
      stripped === 'pasa siguiente' ||
      stripped === 'siguiente' ||
      stripped === 'pasa'
    ) {
      return 'pass';
    }
    return null;
  }

  /** Whole-phrase match con word boundaries — "quiero responder" NO matchea
   *  dentro de "quiero responderme la pregunta". */
  private containsPhrase(haystack: string, phrase: string): boolean {
    const p = this.normalize(phrase);
    if (!p) return false;
    const escaped = p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(?:^|\\s)${escaped}(?:\\s|$)`).test(haystack);
  }

  /** Quita muletillas iniciales normalizadas (bueno/este/pues/…). */
  private stripLeadingFillers(words: string[]): string[] {
    let start = 0;
    while (
      start < words.length &&
      ThinkingVoiceCommandService.LEADING_FILLERS.has(words[start])
    ) {
      start++;
    }
    return words.slice(start);
  }

  /** Dispatch único por ventana (dedupe + log con la fuente instant/instant-tail/llm). */
  private dispatchCommand(
    command: ThinkingCommand,
    source: 'instant' | 'instant-tail' | 'llm',
    onMatch: (commandId: string) => void,
  ): void {
    if (this.wasAlreadyDispatchedThisWindow(command)) {
      console.log('[FC] voice_command_match_duplicate_suppressed', command);
      return;
    }
    this.dispatchedThisWindow.add(command);
    console.log(`[FC] voice_command_match ${command} (${source})`);
    this.ngZone.run(() => onMatch(command));
  }

  /**
   * El mic volvió (permiso re-otorgado / track re-adquirido). Sin mic cada start
   * termina al instante y los reintentos se agotan en ms, dejando la ventana sin
   * ASR (sin comandos ni transcript de respuesta). Si la ventana sigue abierta,
   * relanza con presupuesto de reintentos fresco y la misma fase.
   */
  resumeAfterMicRecovery(): void {
    if (!this.lastOnMatch) return;
    if (this.active) return;
    this.restartCount = 0;
    console.log('[FC] voice_command_resumed_after_mic_recovery');
    this.attemptStart(this.lastOnMatch, this.lastOnTranscript);
  }

  /** `false` → 'off'; `true` → 'thinking'. Compat con callers (HIGH). */
  setCommandMatchingActive(active: boolean): void {
    this.setCommandMatchingMode(active ? 'thinking' : 'off');
  }

  setCommandMatchingMode(mode: CommandMatchingMode): void {
    // Cambiar de fase invalida cualquier classify LLM en vuelo (stale-drop) y
    // el pendiente — un comando del piense no debe dispararse en la respuesta.
    if (mode !== this.mode) {
      this.generation++;
      this.pendingFinal = null;
    }
    this.mode = mode;
  }

  stop(): void {
    this.active = false;
    this.generation++;
    this.pendingFinal = null;
    this.lastOnMatch = null;
    this.lastOnTranscript = null;
    this.isEchoPredicate = null;
    this.setRestarting(false);
    this.onRestartingChange = null;
    this.restartCount = 0;
    this.classifyInFlight = false;
    this.dispatchedThisWindow.clear();
    const recognition = this.recognition;
    this.recognition = null;
    if (!recognition) return;
    try {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      recognition.stop();
    } catch {
      /* ignore — degradación obligatoria */
    }
  }

  private wasAlreadyDispatchedThisWindow(commandId: string): boolean {
    return this.dispatchedThisWindow.has(commandId);
  }

  private normalize(text: string): string {
    return ThinkingVoiceCommandService.normalizeText(text);
  }

  private static normalizeText(text: string): string {
    return (text || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .trim();
  }

  /**
   * Quita del INICIO del texto del ASR el comando answer_now
   * ("(eva/ya) quiero contestar/responder") y devuelve SOLO el resto — la
   * respuesta real del candidato. Solo strippea si el prefijo consumido contiene
   * "quiero" Y un verbo del comando; si no, devuelve el texto intacto.
   */
  static stripAnswerNowCommand(rawText: string): string {
    const raw = (rawText || '').trim();
    if (!raw) return '';
    const tokens = raw.split(/\s+/);
    let start = 0;
    while (
      start < tokens.length &&
      ThinkingVoiceCommandService.LEADING_FILLERS.has(
        ThinkingVoiceCommandService.normalizeText(tokens[start]).replace(/^[^a-z]+|[^a-z]+$/g, ''),
      )
    ) {
      start++;
    }
    let cut = start;
    let sawQuiero = false;
    let sawVerb = false;
    while (cut < tokens.length) {
      const token = ThinkingVoiceCommandService.normalizeText(tokens[cut]).replace(/^[^a-z]+|[^a-z]+$/g, '');
      if (!ThinkingVoiceCommandService.ANSWER_NOW_TOKENS.has(token)) break;
      if (token === 'quiero') sawQuiero = true;
      if (ThinkingVoiceCommandService.ANSWER_NOW_VERBS.has(token)) sawVerb = true;
      cut++;
    }
    if (!(sawQuiero && sawVerb)) return raw;
    return tokens.slice(cut).join(' ').trim();
  }
}
